import { type DatabaseAgentContract } from '@databases/ai-first-creation-database-agent-types';
import {
  type AddEntryToken,
  CsvErrorCategory,
  type CsvErrorCollector,
  CsvErrorType,
  type EditEntryToken,
  type RemoveEntryToken
} from '@databases/streaming-csv-parser';
import { generateId } from '@databases/utils/utils';

import type { TokenProcessingLifecycle } from './token-processing-lifecycle';
import { type ValueProcessor } from './value-processor';

export class EntryProcessor {
  constructor(
    private readonly agent: DatabaseAgentContract,
    private readonly lifecycle: TokenProcessingLifecycle,
    private readonly valueProcessor: ValueProcessor,
    private rowIdToEntryIdMappings: Map<string, string>,
    private readonly errorCollector?: CsvErrorCollector
  ) {}

  private addError(message: string): void {
    this.errorCollector?.addError(
      message,
      CsvErrorCategory.ENTRY,
      CsvErrorType.VALIDATION
    );
  }

  // TODO: consider view settings when creating an entry. We may need to set default values.
  // https://product-fabric.atlassian.net/browse/CTD-5098
  public handleAddEntryToken(addEntryToken: AddEntryToken) {
    const entry = {
      id: generateId(),
      values: {}
    };
    this.agent.addEntry(entry, addEntryToken.rowNumber);
    this.lifecycle.resetSingleFieldStreaming();
    this.valueProcessor.setCurrentEntryId(entry.id);
  }

  public handleEditEntryToken(editEntryToken: EditEntryToken) {
    this.valueProcessor.clearCurrentEntryId();
    const entryId = this.rowIdToEntryIdMappings.get(String(editEntryToken.rowNumber));

    if (!entryId) {
      this.addError(
        `Entry not found for row ${editEntryToken.rowNumber}. Cannot edit non-existent entry.`
      );
      return;
    }

    this.lifecycle.setEntryOperation('edit');
    this.lifecycle.resetSingleFieldStreaming();
    this.valueProcessor.setCurrentEntryId(entryId);
  }

  public handleRemoveEntryToken(removeEntryToken: RemoveEntryToken) {
    const entryId = this.rowIdToEntryIdMappings.get(String(removeEntryToken.rowNumber));

    if (!entryId) {
      this.addError(
        `Entry not found for row ${removeEntryToken.rowNumber}. Cannot remove non-existent entry.`
      );
      return;
    }

    this.valueProcessor.setCurrentEntryId(entryId);
    this.lifecycle.scheduleEntryDeletion(entryId, () => {
      this.agent.removeEntry(entryId);
      this.valueProcessor.clearCurrentEntryId();
    });
  }
}
